import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react'
import { Box, InputLabel, FormHelperText, TextFieldProps } from '@mui/material'

interface HighlightRange {
  start: number
  end: number
}

interface HighlightedTextareaProps
  extends Omit<TextFieldProps, 'multiline' | 'inputRef'> {
  highlightRange?: HighlightRange | null
}

function escapeHTML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function buildHighlightedHTML(
  text: string,
  range: HighlightRange | null | undefined
): string {
  if (!text && !range) return ''
  if (!range) return escapeHTML(text)

  const before = escapeHTML(text.slice(0, range.start))
  const highlighted = escapeHTML(text.slice(range.start, range.end))
  const after = escapeHTML(text.slice(range.end))

  return `${before}<span data-highlight style="background-color:#fff3cd;border-radius:2px;box-shadow:0 0 0 2px #ffc107">${highlighted}</span>${after}`
}

/** Walk the DOM tree and extract plain text, converting <br> to \n */
function getPlainText(element: HTMLElement): string {
  let text = ''
  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || ''
    } else if (node.nodeName === 'BR') {
      text += '\n'
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Block elements (div, p) that some browsers insert on Enter
      if (
        node !== element &&
        (node.nodeName === 'DIV' || node.nodeName === 'P')
      ) {
        if (text.length > 0 && !text.endsWith('\n')) {
          text += '\n'
        }
      }
      for (let i = 0; i < (node as HTMLElement).childNodes.length; i++) {
        walk((node as HTMLElement).childNodes[i])
      }
    }
  }
  walk(element)
  return text
}

/**
 * A textarea with text highlighting support using a contentEditable div.
 * Single element handles both editing and highlight rendering — no backdrop
 * layer, no double text layout, no scroll/width sync issues.
 */
const HighlightedTextarea = forwardRef<
  HTMLDivElement,
  HighlightedTextareaProps
>(
  (
    {
      highlightRange,
      value,
      onChange,
      label,
      error,
      helperText,
      margin,
      name,
      rows,
      sx,
      disabled,
      placeholder,
      // Destructure TextField-specific props so they don't leak to DOM
      /* eslint-disable @typescript-eslint/no-unused-vars */
      InputLabelProps: _ilp,
      inputProps: _ip,
      fullWidth: _fw,
      InputProps: _IP,
      variant: _v,
      select: _s,
      SelectProps: _sp,
      /* eslint-enable @typescript-eslint/no-unused-vars */
    },
    ref
  ) => {
    const editableRef = useRef<HTMLDivElement>(null)
    const lastTextRef = useRef<string>('')
    const prevHighlightRef = useRef<{
      start: number
      end: number
    } | null>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()
    const hasPendingInput = useRef(false)
    const [isFocused, setIsFocused] = useState(false)

    const textValue = typeof value === 'string' ? value : ''

    useImperativeHandle(ref, () => editableRef.current as HTMLDivElement)

    // Sync DOM only when highlight changes or value changes externally.
    // User typing updates the DOM natively — we never touch innerHTML during typing.
    // When hasPendingInput is true, the parent's value is a stale echo from a
    // previous debounce — ignore it so we don't overwrite newer keystrokes.
    useEffect(() => {
      if (!editableRef.current) return

      const highlightChanged =
        highlightRange?.start !== prevHighlightRef.current?.start ||
        highlightRange?.end !== prevHighlightRef.current?.end
      const textChangedExternally =
        textValue !== lastTextRef.current && !hasPendingInput.current

      prevHighlightRef.current = highlightRange
        ? { start: highlightRange.start, end: highlightRange.end }
        : null

      if (highlightChanged || textChangedExternally) {
        editableRef.current.innerHTML = buildHighlightedHTML(
          textValue,
          highlightRange
        )
        lastTextRef.current = textValue
      }
    }, [textValue, highlightRange])

    // Flush any pending debounced onChange
    const flushOnChange = useCallback(() => {
      clearTimeout(debounceRef.current)
      hasPendingInput.current = false
      if (!editableRef.current) return
      const currentText = getPlainText(editableRef.current)
      lastTextRef.current = currentText
      onChange?.({
        target: { value: currentText, name: name || '' },
      } as React.ChangeEvent<HTMLTextAreaElement>)
    }, [onChange, name])

    // Read plain text from DOM and debounce onChange to avoid
    // re-rendering the entire parent page on every keystroke.
    // The contentEditable div shows text natively — no React needed.
    const handleInput = useCallback(() => {
      if (!editableRef.current) return
      lastTextRef.current = getPlainText(editableRef.current)
      hasPendingInput.current = true
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(flushOnChange, 150)
    }, [flushOnChange])

    // Cleanup debounce timer on unmount
    useEffect(() => {
      return () => clearTimeout(debounceRef.current)
    }, [])

    // Strip rich text on paste — insert as plain text only
    const handlePaste = useCallback((e: React.ClipboardEvent) => {
      e.preventDefault()
      const text = e.clipboardData.getData('text/plain')
      document.execCommand('insertText', false, text)
    }, [])

    // Normalize Enter to <br> instead of browser-specific block elements
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        document.execCommand('insertLineBreak')
      }
    }, [])

    // Scroll to highlight when it changes (user clicked a question card)
    useEffect(() => {
      if (!highlightRange || !editableRef.current) return

      const rafId = requestAnimationFrame(() => {
        const el = editableRef.current
        if (!el) return
        const span = el.querySelector('[data-highlight]') as HTMLElement
        if (!span) return

        const elRect = el.getBoundingClientRect()
        const spanRect = span.getBoundingClientRect()
        const offsetTop = spanRect.top - elRect.top + el.scrollTop
        el.scrollTop = Math.max(0, offsetTop - el.clientHeight / 3)
      })

      return () => cancelAnimationFrame(rafId)
    }, [highlightRange])

    const marginStyles =
      margin === 'normal'
        ? { marginTop: '16px', marginBottom: '8px' }
        : margin === 'dense'
          ? { marginTop: '8px', marginBottom: '4px' }
          : {}

    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          ...marginStyles,
          ...sx,
        }}
      >
        {/* MUI-style floating label */}
        {label && (
          <InputLabel
            shrink
            error={!!error}
            focused={isFocused}
            sx={{
              position: 'absolute',
              top: 0,
              left: '14px',
              transform: 'translate(0, -9px) scale(0.75)',
              transformOrigin: 'top left',
              backgroundColor: '#fff',
              padding: '0 5px',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            {label}
          </InputLabel>
        )}

        {/* Single contentEditable div — edits and highlights in one element */}
        <Box
          ref={editableRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={typeof label === 'string' ? label : undefined}
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            flushOnChange()
            setIsFocused(false)
          }}
          sx={{
            flex: 1,
            border: isFocused ? '2px solid' : '1px solid',
            borderColor: error
              ? 'error.main'
              : isFocused
                ? 'primary.main'
                : 'rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            // Compensate padding for 2px border on focus to prevent content shift
            padding: isFocused ? '15.5px 13px' : '16.5px 14px',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '0.00938em',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            overflow: 'auto',
            outline: 'none',
            color: 'rgba(0, 0, 0, 0.87)',
            backgroundColor: disabled ? 'action.disabledBackground' : '#fff',
            boxSizing: 'border-box',
            ...(rows && {
              minHeight: `calc(${(rows as number) * 1.5}em + 33px)`,
            }),
            '&:hover': {
              ...(!isFocused &&
                !error && {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                }),
            },
            '&:empty::before': placeholder
              ? {
                  content: `"${placeholder}"`,
                  color: 'text.disabled',
                }
              : undefined,
          }}
        />

        {/* Helper/error text */}
        {helperText && (
          <FormHelperText error={!!error} sx={{ mx: '14px', mt: '3px' }}>
            {helperText}
          </FormHelperText>
        )}
      </Box>
    )
  }
)

HighlightedTextarea.displayName = 'HighlightedTextarea'

export default HighlightedTextarea
